<script lang="ts">
  import { env } from '$env/dynamic/public';
  import { tick } from 'svelte';
  import { Button } from '@cio/ui/base/button';
  import { Input } from '@cio/ui/base/input';
  import { Textarea } from '@cio/ui/base/textarea';
  import * as Field from '@cio/ui/base/field';
  import ArrowUpRight from '@lucide/svelte/icons/arrow-up-right';
  import Check from '@lucide/svelte/icons/check';
  import Send from '@lucide/svelte/icons/send';
  import { fly } from 'svelte/transition';

  const PHONE_INPUT_PATTERN = String.raw`\+?[0-9][0-9 \(\)\-]{5,18}[0-9]`;

  let contactName = $state('');
  let email = $state('');
  let phone = $state('');
  let institution = $state('');
  let students = $state<number | undefined>();
  let message = $state('');
  let website = $state('');
  let sending = $state(false);
  let sent = $state(false);
  let error = $state('');
  let confirmation = $state<HTMLDivElement>();
  let transitionDuration = $state(0);

  async function submitEnquiry(event: SubmitEvent) {
    event.preventDefault();
    if (sending) return;

    sending = true;
    error = '';
    transitionDuration =
      matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.dataset.motion === 'paused'
        ? 0
        : 350;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);
    try {
      const apiUrl = env.PUBLIC_API_URL || 'https://api.edvello.com';
      const endpoint = `${apiUrl.replace(/\/$/, '')}/contact`;
      const payload = { contactName, email, phone: phone.trim(), institution, students, message, website };
      const body = JSON.stringify(payload);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: controller.signal
      });
      if (!response.ok) {
        error =
          response.status === 429
            ? 'You have sent several messages. Please try again later or email support@edvello.com.'
            : 'Your message could not be sent. Please try again or email support@edvello.com.';
        return;
      }

      const result = await response.json();
      if (result.success !== true) {
        error = 'Your message could not be sent. Please try again or email support@edvello.com.';
        return;
      }

      sent = true;
      await tick();
      confirmation?.focus();
    } catch {
      error =
        'We could not reach the enquiry service. Your message is still here. Please try again or email support@edvello.com.';
    } finally {
      clearTimeout(timeout);
      sending = false;
    }
  }
</script>

<div class="contact-panel" class:sending class:sent>
  <div class="send-track" aria-hidden="true"><span></span></div>
  {#if sent}
    <div
      class="sent-message"
      role="status"
      tabindex="-1"
      bind:this={confirmation}
      in:fly={{ y: 16, duration: transitionDuration }}
    >
      <span class="success-icon"><Check size={28} /></span>
      <h3>Message sent.</h3>
      <p>Thank you for contacting Edvello. Your message has been sent to our support team. We will reply to {email}.</p>
      <Button
        variant="secondary"
        onclick={() => {
          sent = false;
          message = '';
        }}>Send another message</Button
      >
    </div>
  {:else}
    <form onsubmit={submitEnquiry} aria-label="Contact Edvello" aria-busy={sending}>
      <Field.Group>
        <Field.Set>
          <Field.Legend>Tell us about your institution</Field.Legend>
          <Field.Description>Share your questions. We will reply to your email.</Field.Description>
          <Field.Group>
            <div class="field-pair">
              <Field.Field
                ><Field.Label for="contact-name">Your name</Field.Label><Input
                  id="contact-name"
                  name="contactName"
                  autocomplete="name"
                  required
                  minlength={2}
                  maxlength={100}
                  bind:value={contactName}
                  disabled={sending}
                /></Field.Field
              >
              <Field.Field
                ><Field.Label for="contact-email">Email address</Field.Label><Input
                  id="contact-email"
                  name="email"
                  type="email"
                  autocomplete="email"
                  required
                  maxlength={254}
                  bind:value={email}
                  disabled={sending}
                /></Field.Field
              >
            </div>
            <div class="field-pair">
              <Field.Field
                ><Field.Label for="contact-phone">Phone or WhatsApp <span>(optional)</span></Field.Label><Input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  inputmode="tel"
                  autocomplete="tel"
                  placeholder="+91 98765 43210"
                  pattern={PHONE_INPUT_PATTERN}
                  title="Use digits, spaces, brackets or dashes, with an optional + country code"
                  maxlength={20}
                  bind:value={phone}
                  disabled={sending}
                /></Field.Field
              >
              <Field.Field
                ><Field.Label for="contact-students">Students <span>(optional)</span></Field.Label><Input
                  id="contact-students"
                  name="students"
                  type="number"
                  inputmode="numeric"
                  min={1}
                  max={10000000}
                  step={1}
                  bind:value={students}
                  disabled={sending}
                /></Field.Field
              >
            </div>
            <Field.Field
              ><Field.Label for="contact-institution">Institution name</Field.Label><Input
                id="contact-institution"
                name="institution"
                autocomplete="organization"
                required
                minlength={2}
                maxlength={160}
                bind:value={institution}
                disabled={sending}
              /></Field.Field
            >
            <Field.Field
              ><Field.Label for="contact-message">How can we help?</Field.Label><Textarea
                id="contact-message"
                name="message"
                rows={4}
                required
                minlength={10}
                maxlength={4000}
                bind:value={message}
                disabled={sending}
              /></Field.Field
            >
            <div class="contact-trap" aria-hidden="true">
              <Field.Label for="contact-website">Leave this field empty</Field.Label><Input
                id="contact-website"
                name="website"
                tabindex={-1}
                autocomplete="off"
                bind:value={website}
              />
            </div>
          </Field.Group>
        </Field.Set>
      </Field.Group>
      {#if error}<p class="form-error" role="alert" in:fly={{ y: 6, duration: Math.min(180, transitionDuration) }}>
          {error}
        </p>{/if}
      <Button type="submit" disabled={sending} class="contact-send"
        >{#if sending}<span class="send-flight"><Send size={16} /></span> Sending…{:else}Send message <ArrowUpRight
            size={17}
          />{/if}</Button
      >
      <p class="privacy-note">We use your details to respond to this enquiry. <a href="/privacy">Privacy policy</a></p>
      <noscript><p>Email <a href="mailto:support@edvello.com">support@edvello.com</a> to contact us.</p></noscript>
    </form>
  {/if}
</div>

<style>
  .send-track {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    overflow: hidden;
    opacity: 0;
    border-radius: 0 0 18px 18px;
    transition: opacity 200ms;
  }
  .sending .send-track {
    opacity: 1;
  }
  .send-track span {
    display: block;
    height: 100%;
    width: 35%;
    background: linear-gradient(90deg, transparent, #c2d1ff, transparent);
  }
  .sending .send-track span {
    animation: message-travel 1.4s ease-in-out infinite;
  }
  .send-flight {
    display: inline-flex;
    animation: message-flight 1.2s ease-in-out infinite;
  }
  .sent .success-icon {
    animation: message-arrived 450ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  @keyframes message-travel {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(385%);
    }
  }
  @keyframes message-flight {
    0%,
    100% {
      transform: translate(0, 0);
      opacity: 1;
    }
    45% {
      transform: translate(5px, -5px);
      opacity: 0.4;
    }
    46% {
      transform: translate(-4px, 4px);
      opacity: 0.4;
    }
  }
  @keyframes message-arrived {
    from {
      transform: scale(0.8);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
  :global(html[data-motion='paused']) .send-flight,
  :global(html[data-motion='paused']) .send-track span,
  :global(html[data-motion='paused']) .success-icon {
    animation: none;
  }
  @media (prefers-reduced-motion: reduce) {
    .send-flight,
    .send-track span,
    .success-icon {
      animation: none !important;
    }
  }

  .contact-panel {
    position: relative;
    text-align: left;
    padding: 30px;
    background: #ffffff09;
    border: 1px solid #ffffff24;
    border-radius: 18px;
    color: #f0f4ff;
  }
  .field-pair {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }
  .contact-panel :global([data-slot='field-legend']) {
    font-family: Geist, sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #f0f4ff;
  }
  .contact-panel :global([data-slot='field-description']) {
    font-size: 12px;
    line-height: 1.7;
    color: #bac9e4;
  }
  .contact-panel :global(input),
  .contact-panel :global(textarea) {
    background: #ffffff0b;
    color: #fff;
    border-color: #ffffff38;
    min-height: 46px;
    border-radius: 7px;
    font-size: 14px;
    box-shadow: none;
    transition:
      background 200ms,
      border-color 200ms,
      box-shadow 200ms;
  }
  .contact-panel :global(textarea) {
    min-height: 112px;
    resize: vertical;
  }
  .contact-panel :global(input:focus-visible),
  .contact-panel :global(textarea:focus-visible) {
    border-color: #a7baff;
    background: #ffffff13;
    box-shadow: 0 0 0 3px #92adff26;
    outline: none;
  }
  .contact-panel :global(label) {
    color: #e3eaff;
    font-size: 12px;
    font-weight: 450;
  }
  .contact-panel :global(label span) {
    color: #b4c2dc;
    font-size: 10px;
  }
  .contact-panel :global([data-slot='field-group']) {
    gap: 19px;
  }
  .contact-panel :global(.contact-send) {
    width: 100%;
    min-height: 48px;
    background: #e9eeff;
    color: #163371;
    border-radius: 7px;
    margin-top: 23px;
    font-size: 13px;
    font-weight: 550;
    gap: 12px;
    transition: background 200ms;
  }
  .contact-panel :global(.contact-send:hover) {
    background: #f4edac;
  }
  .form-error {
    font-size: 12px;
    line-height: 1.7;
    color: #ffd0c0;
    margin: 18px 0 0;
  }
  .privacy-note {
    color: #b4c2dc;
    font-size: 10px;
    line-height: 1.7;
    margin: 13px 0 0;
  }
  .privacy-note a {
    color: #d4dfff;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .contact-trap {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  .sent-message {
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: center;
    min-height: 400px;
    outline: none;
  }
  .success-icon {
    width: 60px;
    height: 60px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    color: #c3ead7;
    background: #c3ead71a;
    margin-bottom: 23px;
  }
  .sent-message h3 {
    font-size: 31px;
    font-weight: 500;
    letter-spacing: -0.035em;
    margin: 0 0 15px;
  }
  .sent-message p {
    font-size: 14px;
    line-height: 1.8;
    color: #c2cee4;
    margin-bottom: 25px;
  }
  @media (max-width: 760px) {
    .contact-panel {
      padding: 23px 19px;
      border-radius: 14px;
    }
    .field-pair {
      grid-template-columns: 1fr;
    }
    .contact-panel :global(input),
    .contact-panel :global(textarea) {
      font-size: 16px;
      min-height: 48px;
    }
    .contact-panel :global(label) {
      font-size: 13px;
    }
    .contact-panel :global([data-slot='field-legend']) {
      font-size: 17px;
    }
    .privacy-note {
      font-size: 11px;
    }
  }
</style>
